import * as classNames from 'classnames';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

// Styles
const s = require('./ContentEditable.css');

// Props
interface IContentEditableProps {
    onChange?: (html: string, text: string, isLimitExceeded: boolean) => void;
    className?: string;
    classNameForField?: string;
    html?: string | null;
    placeholder?: string | null;
    charactersLimit?: number | null;
    autoFocus?: boolean;
    selectTextOnFirstFocus?: boolean;
    contentEditable: boolean;
}

// Component
@observer
export class ContentEditable extends React.Component<
    IContentEditableProps,
    {}
> {
    static get defaultProps(): IContentEditableProps {
        return {
            onChange: null,
            className: null,
            classNameForField: null,
            html: null,
            placeholder: null,
            charactersLimit: null,
            autoFocus: false,
            selectTextOnFirstFocus: false,
            contentEditable: false,
        };
    }

    constructor(props: IContentEditableProps) {
        super(props);

        this.content = this.props.html
            ? this.checkIfStringIsHtml(this.props.html)
                ? this.props.html
                : '<p>' + this.addLineBreaksToText(this.props.html) + '</p>'
            : this.placeholderHtml;
    }

    private field: HTMLDivElement = null;

    private placeholderClassName = 'contentEditablePlaceholderParagraph';

    @observable private focusCount: number = 0;
    @observable private content: string = '';

    @observable private lastCaretOffset: number = 0;
    @observable private lastCaretNodesIndexes: any[] = [];

    @observable private isBeyondCharacterLimit: boolean = false;

    @computed private get placeholderHtml(): string {
        return (
            '<p class="' +
            this.placeholderClassName +
            '">' +
            (this.props.placeholder ? this.props.placeholder.trim() : '') +
            '</p>'
        );
    }

    public render() {
        const { contentEditable } = this.props;

        return (
            <div className={classNames(s.container, this.props.className)}>
                <div
                    ref={this.referenceField}
                    className={classNames(
                        s.field,
                        this.props.classNameForField
                    )}
                    onInput={this.handleChange}
                    onPaste={this.handlePaste}
                    onFocus={this.handleFocus}
                    onBlur={this.handleBlur}
                    contentEditable={contentEditable}
                    dangerouslySetInnerHTML={{
                        __html: this.content,
                    }}
                />

                <div
                    aria-hidden={!this.isBeyondCharacterLimit}
                    className={classNames(s.error, {
                        [s.show]: this.isBeyondCharacterLimit,
                    })}
                >
                    <p>
                        {`Character limit of ${this.props.charactersLimit ||
                            1} exceeded`}
                    </p>
                </div>
            </div>
        );
    }

    public componentDidMount() {
        // Limit characters count
        if (
            this.props.charactersLimit !== null &&
            this.field &&
            this.field.innerText.length > this.props.charactersLimit
        ) {
            this.isBeyondCharacterLimit = true;

            if (this.props.html) {
                this.propagateChange(this.props.html);
            }
        }

        // When autofocus is true, select whole content of the container
        if (this.props.autoFocus && this.props.contentEditable && this.field) {
            this.focusOnContainerAndSelectAll();
            this.focusCount++;
        }
    }

    public shouldComponentUpdate(nextProps: IContentEditableProps) {
        if (nextProps.html !== this.content) {
            return true;
        } else if (this.field && nextProps.html !== null) {
            // return this.container.innerText.trim() !== this.getTextFromHtml(nextProps.html).trim();
        }

        return (
            this.props.className !== nextProps.className ||
            this.props.onChange !== nextProps.onChange ||
            this.props.contentEditable !== nextProps.contentEditable
        );
    }

    public componentWillReceiveProps(nextProps: IContentEditableProps) {
        if (
            this.props.html !== nextProps.html &&
            (typeof nextProps.html === 'undefined' ||
                nextProps.html === null ||
                nextProps.html === '')
        ) {
            this.content = '';
            this.isBeyondCharacterLimit = false;
        }
    }

    public componentDidUpdate(prevProps: IContentEditableProps) {
        if (
            this.field &&
            prevProps.html !== this.content &&
            this.content !== this.placeholderHtml
        ) {
            this.restoreSelection();
        }

        if (
            prevProps.contentEditable === false &&
            this.props.contentEditable === true &&
            this.field &&
            this.props.autoFocus
        ) {
            setTimeout(() => {
                this.focusOnContainerAndSelectEnd();
            }, 128);
        }

        if (this.content === '') {
            this.field.innerHTML = this.placeholderHtml;
        }
    }

    private selection: any = null;

    private referenceField = (ref: HTMLDivElement) => (this.field = ref);

    private propagateChange = (value: string) => {
        // Get plain text
        const text = this.field.innerText;

        // Check if text is too long
        this.isBeyondCharacterLimit =
            this.props.charactersLimit !== null
                ? text.length > this.props.charactersLimit
                : false;

        // Update html content
        this.content = value;

        // Let parent component know about the change inv alue
        if (this.props.onChange) {
            this.props.onChange(value, text, this.isBeyondCharacterLimit);
        }
    };

    private handleChange = async (e: React.ChangeEvent<HTMLDivElement>) => {
        if (this.field && this.props.onChange) {
            e.preventDefault();
            this.saveSelection();
            const val = this.field.innerHTML;
            this.propagateChange(val);
        }
    };

    private handlePaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
        if (this.field && this.props.onChange) {
            const val = this.field.innerHTML;
            this.propagateChange(val);
        }
    };

    private handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
        if (this.props.selectTextOnFirstFocus && this.focusCount === 0) {
            this.focusOnContainerAndSelectAll();
        } else if (this.placeholderHtml === this.field.innerHTML) {
            this.focusOnContainerAndSelectAll();
        }

        this.focusCount++;
    };

    private handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        if (this.props.placeholder) {
            const currentText = this.field.innerText.trim();

            if (currentText === '') {
                this.field.innerHTML = this.placeholderHtml;
            }
        }
    };

    private focusOnContainerAndSelectAll = () => {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(this.field);
        selection.removeAllRanges();
        selection.addRange(range);
    };

    private focusOnContainerAndSelectEnd = () => {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(this.field);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
    };

    private saveSelection = () => {
        if (this.field) {
            const range = window.getSelection().getRangeAt(0);
            const preCaretRange = range.cloneRange();
            preCaretRange.selectNodeContents(this.field);
            preCaretRange.setEnd(range.endContainer, range.endOffset);

            let childNodeIndexes: number[] = [];
            let endContainerNode = preCaretRange.endContainer;

            if (
                endContainerNode.parentElement.classList.contains(
                    this.placeholderClassName
                )
            ) {
                endContainerNode.parentElement.classList.remove(
                    this.placeholderClassName
                );
            }

            while (endContainerNode !== this.field) {
                const endContainerNodeParent = endContainerNode.parentNode;
                let findIndex = -1;
                for (
                    let i = 0;
                    i < endContainerNodeParent.childNodes.length;
                    i++
                ) {
                    const childNode = endContainerNodeParent.childNodes.item(i);
                    if (childNode === endContainerNode) {
                        findIndex = i;
                        childNodeIndexes = [i].concat(childNodeIndexes);
                        break;
                    }
                }

                endContainerNode = endContainerNodeParent;
            }

            this.lastCaretOffset = preCaretRange.endOffset;
            this.lastCaretNodesIndexes =
                childNodeIndexes.length > 0 ? childNodeIndexes : [0];
        }
    };

    private restoreSelection = () => {
        if (this.field) {
            const selection = window.getSelection();
            const range = document.createRange();
            if (this.lastCaretNodesIndexes.length > 0) {
                // Iterate children nodes until
                let node: Node = this.field;
                if (!node || !node.childNodes || node.childNodes.length <= 0) {
                    return;
                }

                this.lastCaretNodesIndexes.forEach(index => {
                    node = node.childNodes.item(index);
                });
                range.setStart(
                    node.childNodes.length > 0 ? node.childNodes.item(0) : node,
                    this.lastCaretOffset
                );
            } else if (this.field.lastChild) {
                range.setStart(
                    this.field.lastChild.childNodes.length > 0
                        ? this.field.lastChild.childNodes.item(0)
                        : this.field.lastChild,
                    0
                );
            } else {
                range.setStart(
                    this.field.childNodes.length > 0
                        ? this.field.childNodes.item(0)
                        : this.field,
                    0
                );
            }

            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    };

    private checkIfStringIsHtml = (text: string): boolean => {
        try {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            return [].slice
                .call(doc.body.childNodes)
                .some(node => node.nodeType === 1);
        } catch (err) {
            return false;
        }
    };

    private getTextFromHtml = (text: string): string => {
        try {
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const html = doc.childNodes[0];
            const body = html.childNodes[1] as HTMLBodyElement;
            return body.innerText;
        } catch (err) {
            return '';
        }
    };

    private addLineBreaksToText = (text: string): string => {
        return text
            .replace(/\n\s*\n\s*\n/g, '\n\n')
            .replace(/(?:\r\n|\r|\n)/g, '<br>');
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/components/content/ContentEditable.tsx